import type { SxProps, Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button, LoadingButton } from "ui/button";
import { Dialog, DialogActions } from "ui/dialog";
import { Typography } from "ui/typography";
import { Select, type OptionType } from "ui/select";
import { TruncatedText } from "ui/truncated-text";
import TouchpointIcon from "containers/admin/clients/touchpoint/components/touchpoint-icon/touchpoint-icon";
import CampaignFolderSelect from "components/partials/campaign-folder-select/campaign-folder-select";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

type SelectedTouchpointsCampaignModalProps = {
  onCloseCallback: () => void;
  onConfirmCallback: () => void;
  selectedList: TouchpointAttributes[];
  campaignOptions: OptionType[];
  campaignId: string;
  setCampaignId: (campaignId: string) => void;
  folderId: string;
  setFolderId: (folderId: string) => void;
  clientId: string;
  isSubmitting: boolean;
};

const SELECTED_ITEMS_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  width: "100%",
};

const SELECTED_ITEMS_LABEL_SX: SxProps<Theme> = (theme) => ({
  color: theme.palette.grey[900],
});

/** Height of the scroll frame in Figma (node 20753:5160); longer selections scroll. */
const SELECTED_ITEMS_LIST_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  maxHeight: 104,
  overflowY: "auto",
  m: 0,
  p: 0,
  listStyle: "none",
};

const SELECTED_ITEM_SX: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  minWidth: 0,
  ...theme.typography.body1,
  color: theme.palette.grey[900],
});

const SELECTED_ITEM_ICON_SX: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexShrink: 0,
  color: theme.palette.grey[600],
});

export const SelectedTouchpointsCampaignModal = ({
  onCloseCallback,
  onConfirmCallback,
  selectedList,
  campaignOptions,
  campaignId,
  setCampaignId,
  folderId,
  setFolderId,
  clientId,
  isSubmitting,
}: SelectedTouchpointsCampaignModalProps) => {
  return (
    <Dialog
      open
      onClose={onCloseCallback}
      closable={!isSubmitting}
      headline="Move to campaign"
      description={`Please choose the campaign where you would like to move the selected ${
        selectedList.length > 1 ? "items" : "item"
      }.`}
      actions={
        <DialogActions
          primary={
            <LoadingButton
              size="large"
              state={isSubmitting ? "loading" : "default"}
              loadingMessage="Moving to campaign..."
              disabled={!campaignId || isSubmitting}
              onClick={onConfirmCallback}
              data-testid="modal-confirm-button"
            >
              Move to campaign
            </LoadingButton>
          }
          secondary={
            <Button
              size="large"
              variant="outlined"
              onClick={onCloseCallback}
              disabled={isSubmitting}
              data-testid="modal-cancel-button"
            >
              Cancel
            </Button>
          }
        />
      }
    >
      <Select
        label="Associated campaign"
        selectName="move-to-campaign-select"
        ariaLabel="Choose campaign"
        required
        options={campaignOptions}
        value={campaignOptions.find((option) => option.value === campaignId) ?? null}
        onChange={(option) => setCampaignId(option ? String(option.value) : "")}
      />

      <CampaignFolderSelect
        selectName="move-to-campaign-folder-select"
        label="Folder"
        placeholder=""
        ariaLabel="Choose folder"
        value={folderId}
        onChange={setFolderId}
        clientId={clientId}
        campaignId={campaignId}
      />

      <Box sx={SELECTED_ITEMS_SX}>
        <Typography variant="body1" sx={SELECTED_ITEMS_LABEL_SX}>
          Selected item(s)
        </Typography>
        <Box component="ul" sx={SELECTED_ITEMS_LIST_SX}>
          {selectedList.map((item) => (
            <Box component="li" key={item.id} sx={SELECTED_ITEM_SX}>
              <Box component="span" sx={SELECTED_ITEM_ICON_SX}>
                <TouchpointIcon touchpointType={item.type} aria-hidden />
              </Box>
              <TruncatedText text={item.name} />
            </Box>
          ))}
        </Box>
      </Box>
    </Dialog>
  );
};
